import { useCallback, useMemo, useState } from 'react';
import { normalizeSearchPhrase } from '../../../../../../../helpers/normalizeSearchPhrase';
import { useVectorSearchQuery } from '../../../../../../../hooks/api/useAIFeatures';
import type { VectorSearchRequest } from '../../../../../../../lib/api/aiFeatures/dto';

interface UseAIVectorSearchProps {
  vectorField?: string;
}

export const useAIVectorSearch = ({ vectorField }: UseAIVectorSearchProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const searchParams = useMemo<VectorSearchRequest>(
    () => ({
      searchPhrase: searchTerm,
      vector: vectorField ?? '',
      topK: 20,
      resultFilterThreshold: 0.05,
      fractionListsToSearch: 1,
    }),
    [searchTerm, vectorField],
  );

  const { data, isFetching, isLoading } = useVectorSearchQuery(searchParams, {
    enabled: !!searchTerm && !!vectorField,
  });

  const handleSearch = useCallback((value: string) => setSearchTerm(normalizeSearchPhrase(value)), []);

  const filteredResults = useMemo(() => {
    if (isFetching) return [];
    return data?.searchResults?.map((item) => item.filterable_key) ?? [];
  }, [data, isFetching]);

  return {
    handleSearch,
    isSearching: isFetching || isLoading,
    data: filteredResults,
  };
};
